import { useQuery } from "@apollo/client";
import { compact, orderBy } from "lodash";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  GetPreviousMeetingsQueryQuery,
  GetPreviousMeetingsQueryQueryVariables,
  MeetingHeaderFragmentFragment,
} from "types/graphql-schema";

import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import AppLabel from "@components/draft-label/app-label";
import Dropdown from "@components/dropdown/dropdown";
import { DropdownOptionLinkType } from "@components/dropdown/dropdown-types";
import { useLink } from "@components/link/link-helpers";
import Loading from "@components/loading/loading";
import { getTopicflowIcon } from "@components/topicflow-icon/topicflow-icon-helpers";
import useDebounce from "@components/use-debounce/use-debounce";
import {
  assertEdgesNonNull,
  getUrl,
  removeCurrentYear,
} from "@helpers/helpers";

import getPreviousMeetingsQuery from "../graphql/get-previous-meetings-query";

const previousMeetingsLimit = 15;

const PreviousMeetingsPopoverPanel = ({
  meeting,
  hideDateButton = false,
}: {
  meeting: MeetingHeaderFragmentFragment;
  hideDateButton?: boolean;
}) => {
  const meetingGroupId = meeting.meetingGroupId;
  const link = useLink();

  // when hovered, we'll fetch all previous meetings
  const [isHovered, setIsHovered] = useState(false);

  // use ref, so it does not trigger a component render
  const loadingRef = useRef(false);

  // current meeting is the one displayed
  const [currentMeeting, setCurrentMeeting] = useState(meeting);
  const debouncedCurrentMeeting = useDebounce(currentMeeting, 1000);

  // dates
  const meetingDate = removeCurrentYear(
    moment(currentMeeting.startDatetime).format("ddd, ll"),
  );
  const endOfToday = moment().endOf("day").format();
  const maxFutureDate = moment().add(2, "month").endOf("day").format();

  // memoize variables to prevent extra api calls
  const variables = useMemo(
    () => ({
      beforeDate: endOfToday,
      maxFutureDate,
      meetingGroupId: meetingGroupId || -1,
      limit: previousMeetingsLimit,
      after: null,
    }),
    [endOfToday, maxFutureDate, meetingGroupId],
  );
  const { data, loading, fetchMore } = useQuery<
    GetPreviousMeetingsQueryQuery,
    GetPreviousMeetingsQueryQueryVariables
  >(getPreviousMeetingsQuery, {
    variables,
  });

  // compute different meeting data
  const pastMeetings = data?.meetings ? assertEdgesNonNull(data.meetings) : [];
  const nextMeetings = data?.nextMeetings
    ? assertEdgesNonNull(data.nextMeetings)
    : [];
  const allMeetings = orderBy(
    compact([...pastMeetings, ...nextMeetings]),
    ["startDatetime"],
    ["asc"],
  );
  const currentMeetingIndex = allMeetings.findIndex(
    (m) => m.id === currentMeeting.id,
  );
  const hasMorePreviousMeetings = !!data?.meetings?.pageInfo?.hasNextPage;
  const endCursorMorePreviousMeetings = data?.meetings?.pageInfo?.endCursor;

  const firstMeetingInThePast = pastMeetings.find(
    (meeting) => !moment().isSame(meeting.startDatetime, "day"),
  );
  const firstMeetingInFuture = nextMeetings.at(0);
  const meetingOptions: DropdownOptionLinkType[] = compact([
    firstMeetingInFuture,
    ...pastMeetings,
  ]).map((meeting) => {
    return {
      type: "link",
      label: (
        <span className="flex items-center gap-1 justify-between">
          <span className="min-w-36">
            {removeCurrentYear(moment(meeting.startDatetime).format("ddd, ll"))}
          </span>
          {moment().isSame(meeting.startDatetime, "day") ? (
            <AppLabel label="Today" className="bg-blue-100 normal-case" />
          ) : moment().isBefore(meeting.startDatetime, "day") ? (
            <AppLabel
              label="Next Meeting"
              className="bg-purple-100 normal-case"
            />
          ) : firstMeetingInThePast?.id === meeting.id ? (
            <AppLabel label="Last Meeting" className="normal-case" />
          ) : null}
        </span>
      ),
      className: currentMeeting?.id === meeting.id ? "bg-gray-100" : "",
      to: getUrl({
        meetingGroupId: meeting.meetingGroupId,
        meetingId: meeting.id,
      }),
    };
  });
  const meetingBeforeCurrentMeeting = allMeetings[currentMeetingIndex - 1];
  const meetingAfterCurrentMeeting = allMeetings[currentMeetingIndex + 1];

  const handleFetchMorePreviousMeetings = () => {
    // don't fetch more if it's already loading
    if (loadingRef.current) {
      return;
    }
    if (hasMorePreviousMeetings) {
      loadingRef.current = true;
      fetchMore({
        variables: {
          after: endCursorMorePreviousMeetings,
          merge: true,
        },
      })
        .then(() => {
          loadingRef.current = false;
        })
        .catch(() => {
          loadingRef.current = false;
        });
    }
  };

  const handleClickFetchMorePreviousMeetings = (e: any) => {
    e.keepOpen = true;
    handleFetchMorePreviousMeetings();
  };

  // when user changes meeting cursors very quickly
  // we debounce, and redirect to the new meeting after a delay
  useEffect(() => {
    if (debouncedCurrentMeeting.id !== meeting.id) {
      link.redirect(
        getUrl({
          meetingGroupId: debouncedCurrentMeeting.meetingGroupId,
          meetingId: debouncedCurrentMeeting.id,
        }),
      );
    }
  }, [debouncedCurrentMeeting.id]);

  // - if user is looking at a past meeting that is not already loaded
  //   then we'll trigger fetching all previous meetings
  // - if user hovers on the component, we'll fetch all previous meetings
  //   trying to prevent fetching extra meetings
  const currentMeetingIsInPastAndNotInPastMeetings =
    moment().isAfter(currentMeeting.startDatetime) &&
    !pastMeetings.some((m) => m.id === currentMeeting.id);
  const userNeedsMorePastMeetings =
    isHovered || currentMeetingIsInPastAndNotInPastMeetings;
  const loadJustWhatWeNeed = currentMeetingIndex < previousMeetingsLimit * 2; // ensure to have at least one page more than what we need
  const hasMoreMeetingsToLoad =
    hasMorePreviousMeetings && endCursorMorePreviousMeetings;
  const shouldFetchMorePreviousMeetings =
    loadJustWhatWeNeed && userNeedsMorePastMeetings && hasMoreMeetingsToLoad;
  useEffect(() => {
    if (shouldFetchMorePreviousMeetings) {
      handleFetchMorePreviousMeetings();
    }
  }, [shouldFetchMorePreviousMeetings]);

  // update current meeting state when prop changes
  useEffect(() => {
    setCurrentMeeting(meeting);
  }, [meeting.id]);

  useEffect(() => {
    setIsHovered(false);
  }, [meeting.meetingGroupId]);

  return (
    <div className="flex items-center" onMouseEnter={() => setIsHovered(true)}>
      {!hideDateButton && (
        <Dropdown
          menuClassName="max-h-144 bg-white overflow-y-scroll"
          options={compact([
            ...meetingOptions,
            data?.meetings?.pageInfo.hasNextPage && { type: "divider" },
            data?.meetings?.pageInfo.hasNextPage && {
              label: (
                <span className="flex items-center gap-1">
                  <span>Load more</span>
                  {loading && <Loading mini size="4" />}
                </span>
              ),
              onClick: handleClickFetchMorePreviousMeetings,
            },
          ])}
        >
          <Button
            theme={buttonTheme.text}
            size={ButtonSize.medium}
            className="text-left @3xl/layout-header:min-w-[98px]"
          >
            {meetingDate}
          </Button>
        </Dropdown>
      )}
      {/* Calendar dropdown: opens the same meeting-list menu that previously
          lived on the breadcrumb date button. */}
      <Dropdown
        menuClassName="max-h-144 bg-white overflow-y-scroll"
        options={compact([
          ...meetingOptions,
          data?.meetings?.pageInfo.hasNextPage && { type: "divider" },
          data?.meetings?.pageInfo.hasNextPage && {
            label: (
              <span className="flex items-center gap-1">
                <span>Load more</span>
                {loading && <Loading mini size="4" />}
              </span>
            ),
            onClick: handleClickFetchMorePreviousMeetings,
          },
        ])}
      >
        <Button
          theme={buttonTheme.iconGray}
          className="!text-gray-500"
          size={ButtonSize.medium}
          icon={getTopicflowIcon("calendar")}
          aria-label="View all meetings in this series"
        />
      </Dropdown>
      {/* Down arrow: navigate backwards in time (to an older / past
          meeting). Disabled when there is no previous meeting in the
          series. */}
      <Button
        disabled={!meetingBeforeCurrentMeeting}
        size={ButtonSize.medium}
        theme={buttonTheme.iconGray}
        className="!text-gray-500"
        icon={getTopicflowIcon("chevronDown")}
        aria-label="Previous meeting"
        to={
          meetingBeforeCurrentMeeting
            ? getUrl({
                meetingGroupId: meetingBeforeCurrentMeeting?.meetingGroupId,
                meetingId: meetingBeforeCurrentMeeting?.id,
              })
            : undefined
        }
        onClick={(e) => {
          e.preventDefault();
          if (meetingBeforeCurrentMeeting) {
            setCurrentMeeting(meetingBeforeCurrentMeeting);
          }
        }}
      />
      {/* Up arrow: navigate forward in time (to a more recent / future
          meeting). Disabled when there is no future meeting in the series. */}
      <Button
        disabled={!meetingAfterCurrentMeeting}
        size={ButtonSize.medium}
        theme={buttonTheme.iconGray}
        className="!text-gray-500"
        icon={getTopicflowIcon("chevronUp")}
        aria-label="Next meeting"
        to={
          meetingAfterCurrentMeeting
            ? getUrl({
                meetingGroupId: meetingAfterCurrentMeeting?.meetingGroupId,
                meetingId: meetingAfterCurrentMeeting?.id,
              })
            : undefined
        }
        onClick={(e) => {
          e.preventDefault();
          if (meetingAfterCurrentMeeting) {
            setCurrentMeeting(meetingAfterCurrentMeeting);
          }
        }}
      />
      {meeting.id !== currentMeeting.id &&
        currentMeeting.id !== debouncedCurrentMeeting.id && (
          <Loading mini size="4 ml-1" />
        )}
    </div>
  );
};

export default PreviousMeetingsPopoverPanel;
